import {
	type BuildParams as DrawerBuildParams,
	type NavigationRoute,
	buildDrawerNavItems,
} from "@features/dashboard/hooks/useDrawerNavItems/helpers";
import { buildType } from "@helpers/app-identity";
import { Apps } from "@memorang/constants/apps";
import type { Href } from "expo-router";
import type { Action } from "kbar";

type ActionHandlers = {
	onNavigate: (path: Href) => void;
	onTrack: (option: string) => void;
	onShowExamsFilter: () => void;
	onShowSessionConfig: () => void;
	onToggleTheme: () => void;
};

type BuildParams = DrawerBuildParams &
	ActionHandlers & {
		showChangeExam: boolean;
		showQuickStudyAction: boolean;
		isDarkMode: boolean;
	};

const getIconMap = (
	navigationRoutes: NavigationRoute[],
): Record<string, string> => {
	const map: Record<string, string> = {};

	for (const route of navigationRoutes) {
		map[route.name] = route.icon.default;
	}

	map["practice-tests"] = "clipboard-check-outline";
	map["study-pack"] =
		buildType === Apps.pte ? "cube" : "custom:study-pack-outline";
	map.redeem = "gift-outline";

	return map;
};

const getKeywords = (key: string, label: string): string => {
	const labelLower = label.toLowerCase();
	const short = label
		.split(/\s+/)
		.map((word) => word[0])
		.filter(Boolean)
		.join("")
		.toLowerCase();

	return short ? `${labelLower}, ${key}, ${short}` : `${labelLower}, ${key}`;
};

export const buildCommandBarActions = ({
	showChangeExam,
	showQuickStudyAction,
	onNavigate,
	onTrack,
	onShowExamsFilter,
	onShowSessionConfig,
	onToggleTheme,
	isDarkMode,
	...drawerParams
}: BuildParams): Action[] => {
	const { items, routeMap } = buildDrawerNavItems(drawerParams);
	const { t } = drawerParams;
	const iconMap = getIconMap(drawerParams.navigationRoutes);
	const navItems = items.filter((item) => item.key !== "more");

	const navActions: Action[] = navItems.map((item) => {
		const key = item.key;
		const route = routeMap[key];

		return {
			id: key,
			name: item.label,
			icon: iconMap[key] ?? "circle-outline",
			keywords: getKeywords(key, item.label),
			perform: () => {
				onTrack(key);
				if (route) {
					onNavigate(route);
				}
			},
		};
	});

	return [
		...navActions,
		...(showQuickStudyAction
			? [
					{
						id: "custom-study-session",
						name: t("dashboard.header.quickStudy"),
						icon: "text-box-multiple-outline",
						keywords: "custom study session, css",
						section: t("commandBar.sections.quickActions"),
						perform: () => {
							onTrack("custom_study_session");
							onShowSessionConfig();
						},
					},
				]
			: []),
		...(showChangeExam
			? [
					{
						id: "exams",
						name: t("commandBar.actions.changeExam"),
						icon: "circle-edit-outline",
						keywords: "change exam, ce",
						section: t("commandBar.sections.quickActions"),
						perform: () => {
							onTrack("change_exam");
							onShowExamsFilter();
						},
					},
				]
			: []),
		{
			id: "theme",
			name: t(
				isDarkMode
					? "commandBar.actions.switchToLightMode"
					: "commandBar.actions.switchToDarkMode",
			),
			icon: isDarkMode ? "weather-sunny" : "weather-night",
			keywords: "theme, appearance, light mode, dark mode",
			section: t("commandBar.sections.quickActions"),
			perform: () => {
				onTrack("change_theme");
				onToggleTheme();
			},
		},
		{
			id: "profile",
			name: t("dashboard.menu.profile"),
			icon: "account-outline",
			keywords: "profile, pf",
			section: t("account.accountInfo"),
			perform: () => {
				onTrack("profile");
				onNavigate("/profile");
			},
		},
		{
			id: "settings",
			name: t("dashboard.menu.settings"),
			icon: "cog-outline",
			keywords: "settings, s",
			section: t("account.accountInfo"),
			perform: () => {
				onTrack("settings");
				onNavigate("/account/settings");
			},
		},
	];
};
